import { UserInDto } from '../types/inDto';
import { UserEditApiResponse } from '../types/userInDtoGuards';
import { findById } from 'shared/utils/id/findById';
import { isNonEmptyArray } from 'shared/utils/array';

export function normalizeUserEditApiResponseList(
  response: UserEditApiResponse | UserEditApiResponse[]
): UserEditApiResponse[] {
  return Array.isArray(response) ? response : [response];
}

export function extractUserIdFromEditRequestUrl(
  url: string | undefined
): string | undefined {
  if (!url) {
    return undefined;
  }

  const segments = url.split('/');
  const userSegmentIndex = segments.indexOf('user');
  if (userSegmentIndex === -1 || userSegmentIndex + 1 >= segments.length) {
    return undefined;
  }

  return segments[userSegmentIndex + 1].split('?')[0];
}

export function getFirstUserInDtoOrThrow(
  response: UserInDto[],
  context: string
): UserInDto {
  if (response.length === 0) {
    throw new Error(`${context}: expected non-empty user response array`);
  }

  return response[0];
}

export function extractPrimaryUserIdFromEditResponse(
  response: UserEditApiResponse | UserEditApiResponse[]
): string | undefined {
  return normalizeUserEditApiResponseList(response)[0]?._id;
}

export function resolveEditedUserFromEditResponse(
  requestUrl: string | undefined,
  response: UserInDto | UserInDto[]
): UserInDto {
  if (!Array.isArray(response)) {
    return response;
  }

  const editedUserId = extractUserIdFromEditRequestUrl(requestUrl);
  const editedUser = findById(response, editedUserId, 'resolveEditedUserFromEditResponse');
  if (editedUser) {
    return editedUser;
  }

  return getFirstUserInDtoOrThrow(
    response,
    'resolveEditedUserFromEditResponse'
  );
}

export function toSingleUserInDto(
  response: UserInDto | UserInDto[]
): UserInDto {
  if (!Array.isArray(response)) {
    return response;
  }

  return getFirstUserInDtoOrThrow(response, 'toSingleUserInDto');
}

export function isUserUploadErrorResponse(
  response: UserEditApiResponse | UserEditApiResponse[] | { error: unknown }
): response is { error: unknown } {
  return (
    typeof response === 'object'
    && response !== null
    && !Array.isArray(response)
    && 'error' in response
    && !('_id' in response)
  );
}

export function hasUserEditResponseData(
  response: UserEditApiResponse | UserEditApiResponse[]
): boolean {
  return isNonEmptyArray(normalizeUserEditApiResponseList(response));
}
